if (!customElements.get('dl-page-subscription-m')) {
  customElements.define('dl-page-subscription-m', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.button = this.querySelector('.dl-page-subscription-m__submit');
      this.message = this.querySelector('[role="status"]');
      if (!this.form || this.onSubmit) return;
      this.buttonText = this.button.textContent;
      this.onSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit, true);
      this.button.addEventListener('click', this.onSubmit);
      this.onEmailKeydown = (event) => {
        if (event.key === 'Enter' && !event.isComposing) this.handleSubmit(event);
      };
      this.input.addEventListener('keydown', this.onEmailKeydown);
      this.onPageShow = () => this.setSubmitting(false);
      window.addEventListener('pageshow', this.onPageShow);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit, true);
      this.button?.removeEventListener('click', this.onSubmit);
      this.input?.removeEventListener('keydown', this.onEmailKeydown);
      this.onSubmit = null;
      window.removeEventListener('pageshow', this.onPageShow);
    }

    showMessage(state, text) {
      this.message.dataset.state = state;
      this.message.textContent = text;
    }

    async getBraze() {
      // The SDK may load after this section. Wait briefly without navigating.
      for (let attempt = 0; attempt < 100; attempt++) {
        const braze = window.braze;
        if (typeof braze?.changeUser === 'function' && typeof braze?.getUser === 'function' && braze.getUser()) {
          return braze;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      throw new Error('Braze SDK unavailable');
    }

    async syncSubscribeToBraze(email) {
      const groupId = (this.dataset.subscriptionGroupId || '').trim();
      if (!groupId) throw new Error('Email subscription group ID is not configured');
      const braze = await this.getBraze();
      // Select the email's user before writing any subscription attributes.
      const currentUser = braze.getUser();
      if (currentUser?.getUserId?.() !== email && braze.changeUser(email) === false) {
        throw new Error('Braze rejected the user');
      }
      const user = braze.getUser();
      if (!user || typeof user.setEmail !== 'function' ||
          typeof user.setEmailNotificationSubscriptionType !== 'function' ||
          typeof user.addToSubscriptionGroup !== 'function') {
        throw new Error('Braze subscription methods unavailable');
      }
      if (user.setEmail(email) === false) throw new Error('Braze rejected the email');
      user.addAlias?.(email, 'user_email');
      if (user.setEmailNotificationSubscriptionType('opted_in') === false) {
        throw new Error('Braze rejected email opt-in');
      }
      const source = (this.dataset.source || '').trim();
      if (source && (typeof user.setCustomUserAttribute !== 'function' ||
          user.setCustomUserAttribute('subscription_source', source) === false)) {
        throw new Error('Braze rejected the subscription source');
      }
      if (user.addToSubscriptionGroup(groupId) === false) {
        throw new Error('Braze rejected the subscription group');
      }
      // SDK setters enqueue changes; require a successful flush before showing success.
      if (typeof braze.requestImmediateDataFlush !== 'function') throw new Error('Braze flush unavailable');
      await new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => reject(new Error('Braze flush timed out')), 15000);
        try {
          braze.requestImmediateDataFlush((success) => {
            window.clearTimeout(timeout);
            if (success) resolve();
            else reject(new Error('Braze flush failed'));
          });
        } catch (error) {
          window.clearTimeout(timeout);
          reject(error);
        }
      });
    }

    setSubmitting(submitting) {
      this.isSubmitting = submitting;
      this.input.readOnly = submitting;
      this.button.disabled = submitting;
      this.button.textContent = submitting
        ? (this.dataset.submittingText || 'Subscribing…')
        : this.buttonText;
      this.form.setAttribute('aria-busy', String(submitting));
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.isSubmitting) return;
      const email = this.input.value.trim();
      const valid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
      this.input.setAttribute('aria-invalid', String(!valid));
      if (!valid) {
        this.showMessage('error', this.dataset.emailErrorText || 'Please enter a valid email address.');
        this.input.focus();
        return;
      }
      this.input.value = email;
      this.setSubmitting(true);
      this.showMessage('pending', this.dataset.submittingMessage || 'Submitting your subscription, please wait…');
      try {
        await this.syncSubscribeToBraze(email);
        try {
          if (typeof DLPixelH !== 'undefined') {
            Promise.resolve(DLPixelH.capture?.(this.dataset.submitEvent || 'subscribe_submit', { source: this.dataset.source, email })).catch(() => {});
          }
        } catch { /* Best-effort analytics. */ }
        this.showMessage('success', this.dataset.successText || "You're subscribed! Check your inbox for exclusive offers.");
      } catch {
        this.showMessage('error', this.dataset.errorText || 'Subscription failed. Please try again later.');
      } finally {
        this.setSubmitting(false);
      }
    }
  });
}
